import { useEffect, useRef, useState } from "react";
import { Modal } from "../../ui/modal";
import { CryptoTransaction, getNetworkInfo } from "../types";
import { fetchCryptoTransaction } from "../API/services";
import { TokenIcon, NetworkIcon } from "../CryptoIcons";
import Spinner from "../../ui/Spinner/Spinner";
import jsPDF from "jspdf";
import {
  CheckCircle,
  XCircle,
  Clock,
  Copy,
  CheckCircle2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Hash,
  Layers,
  Download,
  Loader2,
  ChevronDown,
  FileText,
  Image as ImageIcon,
} from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  transactionId: string | null;
}

interface DetailRow {
  label: string;
  value: string;
}

const CryptoTransactionSummary = ({ isOpen, onClose, transactionId }: Props) => {
  const [transaction, setTransaction] = useState<CryptoTransaction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !transactionId) {
      if (!isOpen) {
        setTransaction(null);
        setError(null);
        setCopiedField(null);
        setMenuOpen(false);
      }
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const tx = await fetchCryptoTransaction(transactionId);
        setTransaction(tx);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Failed to load transaction");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, transactionId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const truncateMiddle = (str: string, chars = 8) =>
    str.length > chars * 2 ? `${str.slice(0, chars)}…${str.slice(-chars)}` : str;

  const getStatusConfig = (status: CryptoTransaction["status"]) => {
    switch (status) {
      case "PROCESSING":
        return {
          color: "text-blue-600 dark:text-blue-400",
          bg: "bg-blue-100 dark:bg-blue-900/30",
          rgb: [37, 99, 235] as [number, number, number],
          rgbBg: [219, 234, 254] as [number, number, number],
          hex: "#2563eb",
          hexBg: "#dbeafe",
          title: "Processing",
          icon: <Loader2 className="h-4 w-4 animate-spin" />,
        };
      case "CONFIRMED":
        return {
          color: "text-green-600 dark:text-green-400",
          bg: "bg-green-100 dark:bg-green-900/30",
          rgb: [22, 163, 74] as [number, number, number],
          rgbBg: [220, 252, 231] as [number, number, number],
          hex: "#16a34a",
          hexBg: "#dcfce7",
          title: "Confirmed",
          icon: <CheckCircle className="h-4 w-4" />,
        };
      case "PENDING":
        return {
          color: "text-yellow-600 dark:text-yellow-400",
          bg: "bg-yellow-100 dark:bg-yellow-900/30",
          rgb: [202, 138, 4] as [number, number, number],
          rgbBg: [254, 249, 195] as [number, number, number],
          hex: "#ca8a04",
          hexBg: "#fef9c3",
          title: "Pending",
          icon: <Clock className="h-4 w-4" />,
        };
      case "FAILED":
        return {
          color: "text-red-600 dark:text-red-400",
          bg: "bg-red-100 dark:bg-red-900/30",
          rgb: [220, 38, 38] as [number, number, number],
          rgbBg: [254, 226, 226] as [number, number, number],
          hex: "#dc2626",
          hexBg: "#fee2e2",
          title: "Failed",
          icon: <XCircle className="h-4 w-4" />,
        };
    }
  };

  const getTypeLabel = (type: CryptoTransaction["type"]) => {
    switch (type) {
      case "DEPOSIT":
        return "Deposit";
      case "WITHDRAWAL":
        return "Withdrawal";
      case "CHARGES":
        return "Charge";
      case "REVERSAL":
        return "Reversal";
      default:
        return type;
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const loadImageAsDataURL = (
    url: string
  ): Promise<{ dataUrl: string; width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = reject;
      img.src = url;
    });
  };

 const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] => {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";

  const breakLongWord = (word: string): string[] => {
    const parts: string[] = [];
    let chunk = "";
    for (const char of word) {
      const test = chunk + char;
      if (ctx.measureText(test).width > maxWidth && chunk) {
        parts.push(chunk);
        chunk = char;
      } else {
        chunk = test;
      }
    }
    if (chunk) parts.push(chunk);
    return parts;
  };

  words.forEach((word) => {
    if (ctx.measureText(word).width > maxWidth) {
      if (current) {
        lines.push(current);
        current = "";
      }
      const brokenParts = breakLongWord(word);
      lines.push(...brokenParts.slice(0, -1));
      current = brokenParts[brokenParts.length - 1] || "";
      return;
    }

    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
};

  if (!isOpen) return null;

  // DEPOSIT and REVERSAL bring funds in; WITHDRAWAL and CHARGES take funds out
  const isCredit = transaction
    ? transaction.type === "DEPOSIT" || transaction.type === "REVERSAL"
    : false;
  const statusConfig = transaction ? getStatusConfig(transaction.status) : null;
  const networkInfo = transaction ? getNetworkInfo(transaction.network) : null;

  // ---------- Shared data builder ----------

  const getRows = (): DetailRow[] => {
    if (!transaction || !networkInfo) return [];
    const rows: DetailRow[] = [];

    if (transaction.txHash) {
      rows.push({ label: "Transaction Hash", value: transaction.txHash });
    }
    rows.push({ label: "Network", value: networkInfo.name });

    if (transaction.from) {
      rows.push({ label: "From", value: transaction.from });
    }

    if (transaction.to) {
      rows.push({ label: "To", value: transaction.to });
    }

    if (transaction.blockNumber) {
      rows.push({ label: "Block", value: String(transaction.blockNumber) });
    }
    if (transaction.fee > 0) {
      rows.push({ label: "Network Fee", value: `${transaction.fee} ${transaction.token}` });
    }

    return rows;
  };

  // ---------- PDF generation ----------

  const handleDownloadPDF = async () => {
    if (!transaction || !statusConfig || !networkInfo) return;
    setMenuOpen(false);
    setDownloading(true);
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 48;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      try {
        const logo = await loadImageAsDataURL("/images/logo/SecondaryLogoPNG.png");
        const logoMaxWidth = 120;
        const logoMaxHeight = 56;
        const ratio = Math.min(logoMaxWidth / logo.width, logoMaxHeight / logo.height);
        const logoW = logo.width * ratio;
        const logoH = logo.height * ratio;
        doc.addImage(logo.dataUrl, "PNG", pageWidth - margin - logoW, margin - 8, logoW, logoH);
      } catch (e) {
        console.warn("Could not load logo for PDF", e);
      }

      const rows = getRows();

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(17, 24, 39);
      doc.text("Transaction Receipt", margin, y);
      y += 22;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.text(
        `${getTypeLabel(transaction.type)} · ${transaction.token} | ${formatDate(transaction.createdAt)} at ${formatTime(transaction.createdAt)}`,
        margin,
        y
      );
      y += 32;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.setTextColor(17, 24, 39);
      doc.text(
        `${isCredit ? "+" : "-"}${transaction.amount.toLocaleString(undefined, {
          maximumFractionDigits: 8,
        })} ${transaction.token}`,
        margin,
        y
      );
      y += 30;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      const badgeText = statusConfig.title;
      const badgeTextWidth = doc.getTextWidth(badgeText);
      const badgePaddingX = 10;
      const badgeHeight = 20;
      const badgeWidth = badgeTextWidth + badgePaddingX * 2;

      doc.setFillColor(...statusConfig.rgbBg);
      doc.roundedRect(margin, y, badgeWidth, badgeHeight, badgeHeight / 2, badgeHeight / 2, "F");
      doc.setTextColor(...statusConfig.rgb);
      doc.text(badgeText, margin + badgePaddingX, y + badgeHeight / 2 + 3.5);
      y += badgeHeight + 24;

      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;

      const labelSize = 10;
      const valueSize = 10;
      const lineHeight = 14;
      const rowGap = 8;

      const drawRows = (rowsToDraw: DetailRow[]) => {
        rowsToDraw.forEach((row) => {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelSize);
          doc.setTextColor(107, 114, 128);
          doc.text(row.label, margin, y);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueSize);
          doc.setTextColor(17, 24, 39);
          const wrapped: string[] = doc.splitTextToSize(row.value, contentWidth * 0.55);
          wrapped.forEach((line, i) => {
            const lineWidth = doc.getTextWidth(line);
            doc.text(line, pageWidth - margin - lineWidth, y + i * lineHeight);
          });

          y += Math.max(1, wrapped.length) * lineHeight + rowGap;

          if (y > doc.internal.pageSize.getHeight() - 100) {
            doc.addPage();
            y = margin;
          }
        });
      };

      drawRows(rows);
      y += 12;

      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 20;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(156, 163, 175);
      doc.text(`Generated on ${new Date().toLocaleString("en-US")}`, margin, y);
      y += 12;
      doc.text("This is a system-generated receipt.", margin, y);

      doc.save(`receipt-${transaction.txHash || transaction.id}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  // ---------- PNG generation (Canvas) ----------

  const handleDownloadPNG = async () => {
    if (!transaction || !statusConfig || !networkInfo) return;
    setMenuOpen(false);
    setDownloading(true);
    try {
      const width = 640;
      const padding = 40;
      const contentWidth = width - padding * 2;
      const labelFont = "600 13px Arial";
      const valueFont = "400 13px Arial";
      const lineHeight = 20;
      const rowGap = 6;
      const sectionGap = 28;

      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      const logoLoaded = new Promise<boolean>((resolve) => {
        logoImg.onload = () => resolve(true);
        logoImg.onerror = () => resolve(false);
        logoImg.src = "/images/logo/SecondaryLogoPNG.png";
      });
      const hasLogo = await logoLoaded;

      const measureCanvas = document.createElement("canvas");
      const mctx = measureCanvas.getContext("2d")!;

      const rows = getRows();

      const measureRowsHeight = (rowsToMeasure: DetailRow[]) => {
        let h = 0;
        rowsToMeasure.forEach((row) => {
          mctx.font = valueFont;
          const wrapped = wrapText(mctx, row.value, contentWidth * 0.6);
          h += Math.max(1, wrapped.length) * lineHeight + rowGap;
        });
        return h;
      };

      let totalHeight = padding;
      totalHeight += 90;
      totalHeight += 90;
      totalHeight += 20;
      totalHeight += measureRowsHeight(rows);
      totalHeight += sectionGap;
      totalHeight += 60;
      totalHeight += padding;

      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = width * scale;
      canvas.height = totalHeight * scale;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${totalHeight}px`;
      const ctx = canvas.getContext("2d")!;
      ctx.scale(scale, scale);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, totalHeight);

      let y = padding;

      if (hasLogo) {
        const logoMaxWidth = 120;
        const logoMaxHeight = 56;
        const ratio = Math.min(
          logoMaxWidth / logoImg.naturalWidth,
          logoMaxHeight / logoImg.naturalHeight
        );
        const logoW = logoImg.naturalWidth * ratio;
        const logoH = logoImg.naturalHeight * ratio;
        ctx.drawImage(logoImg, width - padding - logoW, y, logoW, logoH);
      }

      ctx.fillStyle = "#111827";
      ctx.font = "700 20px Arial";
      ctx.textBaseline = "top";
      ctx.fillText("Transaction Receipt", padding, y);
      y += 28;

      ctx.fillStyle = "#6b7280";
      ctx.font = "400 12px Arial";
      ctx.fillText(
        `${getTypeLabel(transaction.type)} · ${transaction.token} • ${formatDate(transaction.createdAt)} at ${formatTime(transaction.createdAt)}`,
        padding,
        y
      );
      y += 40;

      ctx.fillStyle = "#111827";
      ctx.font = "700 30px Arial";
      ctx.fillText(
        `${isCredit ? "+" : "-"}${transaction.amount.toLocaleString(undefined, {
          maximumFractionDigits: 8,
        })} ${transaction.token}`,
        padding,
        y
      );
      y += 46;

      const badgeText = statusConfig.title;
      ctx.font = "600 12px Arial";
      const badgeTextWidth = ctx.measureText(badgeText).width;
      const badgePaddingX = 12;
      const badgeHeight = 24;
      const badgeWidth = badgeTextWidth + badgePaddingX * 2;

      const drawRoundedRect = (x: number, yy: number, w: number, h: number, r: number) => {
        ctx.beginPath();
        ctx.moveTo(x + r, yy);
        ctx.arcTo(x + w, yy, x + w, yy + h, r);
        ctx.arcTo(x + w, yy + h, x, yy + h, r);
        ctx.arcTo(x, yy + h, x, yy, r);
        ctx.arcTo(x, yy, x + w, yy, r);
        ctx.closePath();
      };

      ctx.fillStyle = statusConfig.hexBg;
      drawRoundedRect(padding, y, badgeWidth, badgeHeight, badgeHeight / 2);
      ctx.fill();
      ctx.fillStyle = statusConfig.hex;
      ctx.fillText(badgeText, padding + badgePaddingX, y + 6);
      y += badgeHeight + 24;

      ctx.strokeStyle = "#e5e7eb";
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      y += 20;

      const drawRows = (rowsToDraw: DetailRow[]) => {
        rowsToDraw.forEach((row) => {
          ctx.font = labelFont;
          ctx.fillStyle = "#6b7280";
          ctx.fillText(row.label, padding, y);

          ctx.font = valueFont;
          ctx.fillStyle = "#111827";
          const wrapped = wrapText(ctx, row.value, contentWidth * 0.6);
          const valueX = width - padding;
          wrapped.forEach((line, i) => {
            const lineWidth = ctx.measureText(line).width;
            ctx.fillText(line, valueX - lineWidth, y + i * lineHeight);
          });

          y += Math.max(1, wrapped.length) * lineHeight + rowGap;
        });
      };

      drawRows(rows);
      y += sectionGap - rowGap;

      ctx.strokeStyle = "#e5e7eb";
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      y += 20;

      ctx.font = "400 11px Arial";
      ctx.fillStyle = "#9ca3af";
      ctx.fillText(`Generated on ${new Date().toLocaleString("en-US")}`, padding, y);
      y += 16;
      ctx.fillText("This is a system-generated receipt.", padding, y);

      const dataUrl = canvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `receipt-${transaction.txHash || transaction.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6
        dark:border-gray-800 dark:bg-gray-900"
    >
      {loading && (
        <div className="flex h-64 items-center justify-center">
          <Spinner className="h-10 w-10 text-primary" />
        </div>
      )}

      {!loading && error && (
        <div className="flex h-64 flex-col items-center justify-center gap-4 text-center">
          <XCircle className="h-8 w-8 text-red-500" />
          <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2
              text-sm text-gray-700 hover:bg-gray-100
              dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      )}

      {!loading && !error && transaction && statusConfig && networkInfo && (
        <>
          {/* Header */}
          <div className="mb-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                {isCredit ? (
                  <ArrowDownToLine className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <ArrowUpFromLine className="h-5 w-5 text-red-600 dark:text-red-400" />
                )}
              </div>
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-white/90">
                  <TokenIcon token={transaction.token} size={18} />
                  {getTypeLabel(transaction.type)} · {transaction.token}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(transaction.createdAt)} • {formatTime(transaction.createdAt)}
                </p>
              </div>
            </div>

            {/* Amount */}
            <div className="mb-4 text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {isCredit ? "+" : "-"}
                {transaction.amount.toLocaleString(undefined, {
                  maximumFractionDigits: 8,
                })}{" "}
                {transaction.token}
              </div>
              <div className="mt-2">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}
                >
                  {statusConfig.icon}
                  {statusConfig.title}
                </span>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="space-y-4">
            {/* Transaction hash */}
            {transaction.txHash && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Hash className="h-4 w-4 text-gray-400" />
                    Transaction Hash
                  </div>
                  <button
                    onClick={() => copyToClipboard(transaction.txHash!, "hash")}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark transition-colors"
                  >
                    {copiedField === "hash" ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                    {copiedField === "hash" ? "Copied!" : "Copy"}
                  </button>
                </div>
                <div className="font-mono text-sm text-gray-600 dark:text-gray-400 break-all">
                  {truncateMiddle(transaction.txHash, 10)}
                </div>
              </div>
            )}

            {/* Details grid */}
            <div className="grid gap-3">
              <div className="flex items-center justify-between py-2">
                <div className="text-sm text-gray-600 dark:text-gray-400">Type</div>
                <div className="text-sm font-medium text-gray-800 dark:text-white">
                  {getTypeLabel(transaction.type)}
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Layers className="h-4 w-4" />
                  Network
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-white">
                  <NetworkIcon network={transaction.network} size={16} />
                  {networkInfo.name}
                </div>
              </div>

              {transaction.from && (
                <div className="flex items-center justify-between py-2 gap-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">From</div>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-sm font-medium text-gray-800 dark:text-white break-all text-right">
                      {truncateMiddle(transaction.from, 6)}
                    </span>
                    <button
                      onClick={() => copyToClipboard(transaction.from!, "from")}
                      className="text-gray-400 hover:text-primary"
                    >
                      {copiedField === "from" ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                </div>
              )}

               {transaction.to && (
                <div className="flex items-center justify-between py-2 gap-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">To</div>
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-sm font-medium text-gray-800 dark:text-white break-all text-right">
                      {truncateMiddle(transaction.to, 6)}
                    </span>
                    <button
                      onClick={() => copyToClipboard(transaction.to!, "to")}
                      className="text-gray-400 hover:text-primary"
                    >
                      {copiedField === "to" ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {transaction.blockNumber && (
                <div className="flex items-center justify-between py-2">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Block</div>
                  <div className="font-mono text-sm font-medium text-gray-800 dark:text-white">
                    {transaction.blockNumber}
                  </div>
                </div>
              )}

              {transaction.fee > 0 && (
                <div className="flex items-center justify-between py-2">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Network Fee</div>
                  <div className="text-sm font-medium text-gray-800 dark:text-white">
                    {transaction.fee} {transaction.token}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2
                text-sm font-medium text-gray-700 hover:bg-gray-100
                dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
            >
              Close
            </button>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((prev) => !prev)}
                disabled={downloading}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm
                  font-medium text-white hover:opacity-90 transition-opacity
                  flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {downloading ? "Preparing..." : "Download Receipt"}
                {!downloading && <ChevronDown className="h-3.5 w-3.5" />}
              </button>

              {menuOpen && !downloading && (
                <div
                  className="absolute bottom-full right-0 mb-2 w-44 overflow-hidden
                    rounded-lg border border-gray-200 bg-white shadow-lg
                    dark:border-gray-700 dark:bg-gray-800 z-10"
                >
                  <button
                    onClick={handleDownloadPDF}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm
                      text-gray-700 hover:bg-gray-100
                      dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <FileText className="h-4 w-4 text-red-500" />
                    Download as PDF
                  </button>
                  <button
                    onClick={handleDownloadPNG}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-sm
                      text-gray-700 hover:bg-gray-100
                      dark:text-gray-200 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                  >
                    <ImageIcon className="h-4 w-4 text-blue-500" />
                    Download as Image
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default CryptoTransactionSummary;
